import { Component } from '@theme/component';
import { CartUpdateEvent, CartAddEvent } from '@theme/events';

/**
 * A custom element that displays cart items.
 * 
 * @extends {Component}
 */
class CartItemsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // Listen for cart update events
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    
    // Ensure content is visible - use flex to match CSS
    this.style.display = 'flex';
    this.style.flexDirection = 'column';
    this.style.width = '100%';
    this.style.height = '100%';
    
    // Ensure all child elements are visible
    const children = Array.from(this.children);
    children.forEach(child => {
      if (child.style.display === 'none') {
        child.style.display = '';
      }
    });
    
    const wrapper = this.querySelector('.cart-items__wrapper');
    if (wrapper) {
      wrapper.style.display = 'block';
    }
    
    // Ensure cart items are visible
    const cartItems = this.querySelector('.cart-items, .cart-drawer__items');
    if (cartItems) {
      cartItems.style.display = 'block';
    }
    
    // Force visibility of all content
    const allChildren = this.querySelectorAll('*');
    allChildren.forEach(el => {
      if (el.style && el.style.display === 'none') {
        el.style.display = '';
      }
    });
    
    console.log('[cart-items-component] Connected with section ID:', this.sectionId);
    console.log('[cart-items-component] Has content:', this.children.length, 'children');
    console.log('[cart-items-component] Wrapper exists:', !!this.querySelector('.cart-items__wrapper'));
    console.log('[cart-items-component] Cart items exist:', !!this.querySelector('.cart-item'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events
   */
  #handleCartUpdate = async (event) => {
    const sectionId = this.sectionId;
    
    // If no valid section ID, refresh content manually
    if (!sectionId || sectionId === '' || sectionId === 'cart-drawer-fallback') {
      await this.#refreshCartContent();
      return;
    }

    // If event has section HTML, use it
    const cartItemsHtml = event.detail?.data?.sections?.[sectionId];
    if (cartItemsHtml) {
      try {
        const { morphSection } = await import('@theme/section-renderer');
        morphSection(sectionId, cartItemsHtml);
      } catch (error) {
        console.warn('[cart-items-component] Error morphing section, refreshing manually:', error);
        await this.#refreshCartContent();
      }
    } else {
      // Try to render section via API
      try {
        const { sectionRenderer } = await import('@theme/section-renderer');
        await sectionRenderer.renderSection(sectionId, { cache: false });
      } catch (error) {
        console.warn('[cart-items-component] Error rendering section, refreshing manually:', error);
        await this.#refreshCartContent();
      }
    }
  };

  /**
   * Refreshes cart content by fetching HTML
   */
  #refreshCartContent = async () => {
    try {
      const htmlResponse = await fetch('/cart?' + Date.now());
      if (!htmlResponse.ok) {
        throw new Error(`Cart HTML fetch failed: ${htmlResponse.status}`);
      }
      const html = await htmlResponse.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartItemsWrapper = doc.querySelector('.cart-items__wrapper') ||
                                   doc.querySelector('cart-items-component .cart-items__wrapper');
      
      if (newCartItemsWrapper) {
        const currentWrapper = this.querySelector('.cart-items__wrapper');
        if (currentWrapper) {
          currentWrapper.innerHTML = newCartItemsWrapper.innerHTML;
        }
      }
      
      // Update cart count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartCountElements = document.querySelectorAll('[ref="cartItemCount"]');
      cartCountElements.forEach(el => {
        if (el) el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('[cart-items-component] Error refreshing cart content:', error);
    }
  };

  /**
   * Gets the section ID from data attribute or finds it dynamically
   */
  get sectionId() {
    try {
      let sectionId = this.dataset?.sectionId || '';
      
      // If missing, try to find parent section
      if (!sectionId || sectionId === '') {
        const parentSection = this.closest('[id^="shopify-section-"]');
        if (parentSection && parentSection.id) {
          const parentId = parentSection.id;
          if (parentId.startsWith('shopify-section-')) {
            sectionId = parentId.replace('shopify-section-', '');
            this.dataset.sectionId = sectionId;
            return sectionId;
          }
        }
      }
      
      return sectionId || '';
    } catch (error) {
      console.error('[cart-items-component] Error getting section ID:', error);
      return '';
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
